import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../../../environments/environment';
import { Notification } from '../notification/notification.model';
import { NotificationCategory } from './notification-category.model';
import { ToastService } from '../../../shared/toast.service';
import { NotificationService } from './notification.service';
import { SfhSecurityService } from '@sasser/sfh-shared-security';
import { PbiReport } from '../../../../../src/app/views/reporting/pbi-report.model';
import { PbiEmbedService } from '../../../../../src/app/views/reporting/pbi-embed.service';

@ Injectable({providedIn: 'root'})
export class SignalRService {

	private _hubConnection: HubConnection | null;
	private notificationCategories = new Array<NotificationCategory>();
	private dismissedCategories = new Array<NotificationCategory>();
	public groupByCategory = true;
	public activeView = true;
	public activeNotificationFetched: boolean = false;
	public dismissedNotificationFetched: boolean = false;
	public pbiCommercialRpts: PbiReport[];
	public pbiFleetRpts: PbiReport[];


	constructor(
		private toastr: ToastService,
		private notificationService: NotificationService,
		public sfhAuth: SfhSecurityService,
		private pbiEmbedService: PbiEmbedService
	) {	}

	connect(): HubConnection {
		if (!this._hubConnection && this.sfhAuth.isAuthenticated()) {
			let url = 'notification';

			this._hubConnection = new HubConnectionBuilder()
			.withUrl(url, { accessTokenFactory: () => localStorage.getItem('id_token')!})
			.withAutomaticReconnect()
			.configureLogging(signalR.LogLevel.Information)
			.build();

			// work-around:
			Object.defineProperty(WebSocket, 'OPEN', { value: 1, });

			this.startHubConnection();

			this._hubConnection.onclose(() => {
				this._hubConnection = null;
			});

			this.fetchNotifications();
			this.fetchAllPbiReports();
		}

		return this._hubConnection!;
	}

	private startHubConnection(): void {
		if (this._hubConnection) {
			this._hubConnection
				.start()
				.then(() => {
					console.log('Connection started.');
				})
				.catch(err => {
					console.log('Error while starting connection ' + err);
				});
		}

		this._hubConnection?.on('BroadcastNotification', (item: Notification) => {
			console.log('BroadcastNotification fired.');
			var notification = new Notification(item);
			var message = notification.message;
			console.log('Notification converted.');
			this.toastr.info(message, notification.title, {
				positionClass: 'toast-bottom-right',
				timeOut: 10000,
				enableHtml: true,
				easeTime: 0
			});

			var category = this.notificationCategories.find(x => x.name === notification.category);
			if (!category) {
				category = new NotificationCategory(notification.category, []);
				category.notifications = [];
				this.notificationCategories.push(category)
			}
			category.notifications.unshift(notification);
			console.log('BroadcastNotification completed.');
		});
	}

	getNotificationsCategories(): NotificationCategory[] {
		return this.notificationCategories;
	}

	getDismissedCategories(): NotificationCategory[] {
		return this.dismissedCategories;
	}

	getPbiCommercialRpts(): PbiReport[] {
		return this.pbiCommercialRpts;
	}

	getPbiFleetRpts(): PbiReport[] {
		return this.pbiFleetRpts;
	}

	notificationFetched(): boolean {
		return this.activeNotificationFetched || this.dismissedNotificationFetched;
	}

	fetchNotifications(): void {
		if (this.sfhAuth.isAuthenticated()) {
			if (this.activeView) {
				this.notificationCategories = [];
				this.activeNotificationFetched = false;
				this.notificationService.getNotifications(this.sfhAuth.userInfo.email).subscribe(results => {
					this.activeNotificationFetched = true;
					this.notificationCategories = this.processedNotificationCaterogies(results);
				},
				error => {
					this.activeNotificationFetched = true;
					this.toastr.error(error, 'Could not fetch notifications!');
				});
			} else {
				this.dismissedCategories = [];
				this.dismissedNotificationFetched = false;
				this.notificationService.getDismissedNotifications(this.sfhAuth.userInfo.email).subscribe(results => {
					this.dismissedNotificationFetched = true;
					this.dismissedCategories = this.processedNotificationCaterogies(results);
				},
				error => {
					this.dismissedNotificationFetched = true;
					this.toastr.error(error, 'Could not fetch dismissed notifications!');
				});
			}
		}
	}
	processedNotificationCaterogies(results: any): NotificationCategory[]{
		var notifications: Notification[] = [];
		var categories = new Array<NotificationCategory>();
		results.forEach(x => notifications.push(new Notification(x)));
		if (this.groupByCategory){
			var ungroupedCategory = categories.find(x => x.name === 'Notifications');
			if (!!ungroupedCategory) {
				categories.splice(categories.indexOf(ungroupedCategory), 1);
			}

			var distinctCategories = notifications.map(x => x.category).filter((value, index, self) => self.findIndex(x => x == value) === index)
			distinctCategories.forEach(category => {
				var nCategory = categories.find(x => x.name === category);
				if (!nCategory) {
					nCategory = new NotificationCategory(category, []);
					categories.push(nCategory)
				}
				nCategory.notifications = [];
				nCategory.notifications = notifications.filter(x => x.category === category);
			});
		} else {
			var category = new NotificationCategory('Notifications', notifications);
			categories = [category];
		}

		this.removeEmptyCategories();

		return categories;
	}

	fetchAllPbiReports(): void {
		if (this.sfhAuth.isAuthenticated()) {
			this.pbiEmbedService.getAllPbiReports().subscribe(data=> {
				this.pbiCommercialRpts = data.filter(x=>x.area.toLowerCase() == 'commercial');
				this.pbiFleetRpts = data.filter(x=>x.area.toLowerCase() == 'fleet');
			});
		}
	}

	public removeEmptyCategories(): void {
		if( this.activeView ) {
			var emptyCategories = this.notificationCategories.filter(x => x.count === 0);
			emptyCategories.forEach(x => this.notificationCategories.splice(this.notificationCategories.indexOf(x), 1));
		} else {
			var emptyDismissed = this.dismissedCategories.filter(x => x.dismissedCount === 0);
			emptyDismissed.forEach(x => this.dismissedCategories.splice(this.dismissedCategories.indexOf(x), 1));
		}
	}

	public setGroupByCategory(value: boolean): void {
		this.groupByCategory = value;
	}
	public setViewType(value: boolean): void {
		this.activeView = value;
	}
	public getGroupByCategory(): boolean {
		return this.groupByCategory;
	}
}
